import _ from 'lodash'
import icon from 'scoreboard/components/icon'
import calendars from 'scoreboard/components/calendars'
import RelativeOptions from 'scoreboard/components/relative-options'
import EditSerieMenu from './edit-serie-menu'
import FeatureView from '../feature-view'
import ChartSortTypes from 'scoreboard/charts/model/chart-sort-types'
const { MANUAL } = ChartSortTypes
import template from './serie-view.hbs'
import isSerieInErrorState from 'scoreboard/charts/is-serie-in-error-state'

/**
 * @param {ChartDesigner} chart
 * @param {ChartDesignerSerie} serie
 * @return {Object} the periods referenced in the serie
 */
function getPeriodLabelsForSerie(chart, serie) {
	if (_.isEmpty(serie.periods)) {
		return []
	}
	return serie.periods.map(period => {
		if (_.isNil(period.get('periodDelta'))) {
			return calendars.periodsById[period.get('periodId')].label
		} else {
			const calendarId = chart.periodBreakdownCalendarId(period)
			const calendar = calendars.calendarsById[calendarId]
			return RelativeOptions.prototype.label(period.get('periodDelta'), calendar)
		}
	})
}

/**
 * @extends FeatureView
 */
const SerieView = FeatureView.extend(/** @lends SerieView */{

	className: FeatureView.prototype.className + ' button button-default',

	events: _.extend({
		'click': 'onEdit' // calls FeatureView.onEdit()
	}, FeatureView.prototype.events),

	renderContent: function() {
		this.$el.empty().append(template(this.context()))
		this.$el.toggleClass('is-placeholder', this._isPlaceholderOrMissingRollup())
		this.$el.toggleClass('is-serie-error-state', isSerieInErrorState(this.model, this.chart))
		this.currentColor = this.options.model.get('color')
		this.$el.addClass(this.currentColor)
		this.toggleForecast(this.model)
		this.model.on('change', model => {
			this.toggleForecast(model)
			// for single serie charts, changing the serie type should change
			// the plotType of the chart itself
			const newPlotType = _.get(model, 'changed.plotType')
			if (!_.isNil(newPlotType) && newPlotType === 'filled-map'
				|| newPlotType === 'spike-map'
				|| newPlotType === 'bubble-map') {
				this.chart.set('plotType', newPlotType)
			}
		})
	},

	context () {
		return _.extend({
			errorMessage: this.model.get('error')?.message
		}, this.options.model.attributes)
	},

	/**
	 * Toggles the `.is-showing-forecast` class on the serie view which is
	 * used to hide/show the forecast icon
	 */
	toggleForecast: function(model) {
		let forecastModel = model.get('forecastModel')
		let hasForecastType = !_.isNil(forecastModel?.forecastType)
		this.$el.toggleClass('is-showing-forecast', hasForecastType)
	},

	/**
	 * #40352 returns true if this serie is using placeholder data or it is a tree
	 * map chart and a rollup field has not been selected
	 */
	_isPlaceholderOrMissingRollup() {
		if (!_.isNil(this.model.get('placeholderSerie'))) return true
		if (!this.chart.isHierarchical()) return false
		if (!this.model.get('datasetRollupFieldId')) return true

		return false
	},

	/**
	 * Updates the UI with the latest feature config; works either on initial
	 * render or on update
	 */
	paint: function () {
		const label = this.$('.feature-label')
		label.text(this.chart.featureLabel({ feature: this.model }))

		// Update color
		this.$el.removeClass(this.currentColor)
		// Store so we can remove it if its updated

		this.chart.get('colors')
		this.currentColor = this._getColor()
		this.$el.addClass(this.currentColor)

		// Update Icon
		const iconName = this.model.get('plotType')
		let svg = icon(iconName, 'feature-view-icon')
		this.$('.feature-view-icon').replaceWith(svg)

		// Update periods
		const periodLabels = getPeriodLabelsForSerie(this.chart, this.model)
		this.$('.serie-periods').text(periodLabels.join(', '))

		this.renderErrorState()
	},

	_getColor: function() {
		const colorSettings = _.find(this.chart.get('colors'),
			c => c.plotType === this.model.get('plotType'))

		const type = colorSettings ? colorSettings.colorType : MANUAL
		switch (type) {
			case 'specific':
				return colorSettings.specificColor
			case 'score':
			case MANUAL:
				return this.model.get('color')
		}
	},

	/**
	 * @param {Event} e the DOM event that triggered the menu to be shown
	 */
	createEditMenu: function(e) {
		const menu = new EditSerieMenu({
			model: this.chart,
			feature: this.model
		})
		// #40291 Rerender the serie in case an special styling is added or removed
		menu.on('datasource-updated', () => this.render())

		return menu
	}
})

export default SerieView
export { getPeriodLabelsForSerie }

SerieView.__filename__ = 'charts/edit/serie/serie-view.js'
